import { Masonry } from '@mui/lab';
import { Box, useTheme } from '@mui/material';
import { useSpring, animated } from '@react-spring/web';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Task } from '../../tasks/models/task';

import MockTaskCard from './MockTaskCard';

let tasks = Array(4)
    .fill([
        {
            id: '1',
            userId: 'user1',
            title: 'Morning Yoga',
            description: '30-minute stretching routine to start the day.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 3 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 3 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '2',
            userId: 'user2',
            title: 'Weekly Grocery Shopping',
            description: '',
            checklist: [
                { id: '1', text: 'fresh vegetables', completed: false },
                { id: '2', text: 'almond milk', completed: false },
                { id: '3', text: 'pasta', completed: false },
                { id: '4', text: 'coffee', completed: false },
                { id: '5', text: 'snacks', completed: false },
            ],
            dueDate: new Date(
                Date.now() + 4 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 4 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '3',
            userId: 'user3',
            title: 'Client Y Zoom Meeting',
            description:
                'Prepare updated wireframes and set agenda for the meeting at 10 AM.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 5 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 5 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '4',
            userId: 'user4',
            title: 'Submit Logo Concepts',
            description: 'Prepare and send logo drafts for review by 3 PM.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 2 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 2 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '5',
            userId: 'user5',
            title: 'Pay Utility Bills',
            description: 'Pay electricity and water bills before the due date.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 7 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 7 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '6',
            userId: 'user6',
            title: 'Meal Prep for the Week',
            description: '',
            checklist: [
                { id: '1', text: 'Chop vegetables', completed: false },
                { id: '2', text: 'Cook rice', completed: false },
                { id: '3', text: 'Marinate chicken', completed: false },
            ],
            dueDate: new Date(
                Date.now() + 3 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 3 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '7',
            userId: 'user7',
            title: 'Team Standup Meeting',
            description: 'Daily team sync at 9:30 AM.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 2 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 2 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '8',
            userId: 'user8',
            title: 'Clean the Kitchen',
            description: 'Deep clean kitchen surfaces and fridge.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 4 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 4 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '9',
            userId: 'user9',
            title: 'Write Blog Post',
            description: 'Write and edit the draft for the new blog post.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 6 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 6 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
        {
            id: '10',
            userId: 'user10',
            title: 'Take Dog to Vet',
            description: 'Annual health checkup for Bruno.',
            checklist: [],
            dueDate: new Date(
                Date.now() + 8 * 24 * 60 * 60 * 1000
            ).toISOString(), // Due in 8 days
            status: 'new',
            createdAt: new Date().toISOString(),
            archivedAt: null,
            deletedAt: null,
        },
    ])
    .flat();

tasks = tasks.map((task: Task) => ({
    ...task,
    id: uuidv4(),
}));

const AnimatedMasonryBackground: React.FC = () => {
    const theme = useTheme();
    let animate: boolean = true;

    const animation = useSpring({
        from: { transform: 'translateY(0px)' },
        to: async (next) => {
            while (animate) {
                await next({ transform: 'translateY(-100%)' });
                await next({ transform: 'translateY(0px)' });
            }
        },
        config: { duration: 20000 },
    });

    return (
        <Box
            sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                zIndex: 0,
                paddingLeft: 2,
                opacity: theme.palette.mode === 'dark' ? 0.35 : 0.1, // Adjust opacity as needed for subtle background effect
            }}
        >
            <animated.div style={animation}>
                <Masonry
                    columns={{ xs: 2, sm: 2, md: 3, lg: 4, xl: 5 }}
                    spacing={2}
                >
                    {tasks.map((task) => (
                        <Box key={task.id} sx={{ marginBottom: '16px' }}>
                            <MockTaskCard task={task} />
                        </Box>
                    ))}
                </Masonry>
            </animated.div>
        </Box>
    );
};

export default AnimatedMasonryBackground;
